/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Claim Status Codes mapping
 */
import _ from 'lodash';
import { ANT_TAG_COLORS } from '@veritable/common/constants/antd';
import systemMapping from 'app-constants/eligibility/fhirSystemMapping.json';

// claim work states
const CLAIM_WORK_STATES: Record<
	ClaimRequest.IClaimWorkState,
	{ value: ClaimRequest.IClaimWorkState; label: string; tagColor: string; textColor: string }
> = {
	a_processing: {
		value: 'a_processing',
		label: 'Processing',
		tagColor: '#595959',
		textColor: '#595959',
	},
	a_info_req: {
		value: 'a_info_req',
		label: 'Info Required',
		tagColor: '#F0AD4E',
		textColor: '#F0AD4E',
	},
	a_adjudicated: {
		value: 'a_adjudicated',
		label: 'Adjudicated',
		tagColor: '#5A9BD5',
		textColor: '#5A9BD5',
	},
	a_payment_posted: {
		value: 'a_payment_posted',
		label: 'Payment Posted',
		tagColor: '#52C41A',
		textColor: '#52C41A',
	},
	ia_not_accepted: {
		value: 'ia_not_accepted',
		label: 'Not Accepted',
		tagColor: '#E57373',
		textColor: '#E57373',
	},
	ia_closed: {
		value: 'ia_closed',
		label: 'Closed',
		tagColor: '#52C41A',
		textColor: '#52C41A',
	},
	ia_void: {
		value: 'ia_void',
		label: 'Void',
		tagColor: ANT_TAG_COLORS.red,
		textColor: ANT_TAG_COLORS.red,
	},
	ia_void_superseded: {
		value: 'ia_void_superseded',
		label: 'Superseded',
		tagColor: '#9E9E9E',
		textColor: '#9E9E9E',
	},
	archived: {
		value: 'archived',
		label: 'Archived',
		tagColor: '#595959',
		textColor: '#595959',
	},
	ia_superseded: {
		value: 'ia_superseded',
		label: 'Superseded',
		tagColor: '#9E9E9E',
		textColor: '#9E9E9E',
	},
	draft: {
		value: 'draft',
		label: 'Draft',
		tagColor: '#595959',
		textColor: '#595959',
	},
};

const GROUPED_CLAIM_WORK_STATES: Record<
	ClaimRequest.IGroupedClaimWorkState,
	{
		value: ClaimRequest.IClaimWorkState[];
		label: string;
		groupValue: ClaimRequest.IGroupedClaimWorkState;
	}
> = {
	active: {
		value: ['a_processing', 'a_info_req', 'a_adjudicated', 'a_payment_posted'],
		label: 'Active',
		groupValue: 'active',
	},
	inactive: {
		value: ['ia_closed', 'ia_superseded', 'ia_not_accepted', 'ia_void', 'ia_void_superseded'],
		label: 'Inactive',
		groupValue: 'inactive',
	},
	// active and inactive combined
	all: {
		value: [
			'a_processing',
			'a_info_req',
			'a_adjudicated',
			'a_payment_posted',
			'ia_closed',
			'ia_superseded',
			'ia_not_accepted',
			'ia_void',
			'ia_void_superseded',
		],
		label: 'All',
		groupValue: 'all',
	},

	archived: {
		value: ['archived'],
		label: 'Archived',
		groupValue: 'archived',
	},
	draft: {
		value: ['draft'],
		label: 'Draft',
		groupValue: 'draft',
	},
};

// active and inactive claim work states
const ACTIVE_AND_INACTIVE_CLAIM_WORK_STATES = _.pick(GROUPED_CLAIM_WORK_STATES, ['active', 'inactive']);

// claim types
const CLAIM_TYPES: Record<ClaimFiling.IClaimType, { value: ClaimFiling.IClaimType; label: string }> = {
	institutional: { value: 'institutional', label: 'Institutional' },
	professional: { value: 'professional', label: 'Professional' },
};

// related claim code display mapping
const RELATED_CLAIM_CODE_DISPLAY_MAPPING = {
	primary: 'View Primary Claim',
	secondary: 'View Secondary Claim',
	tertiary: 'View Tertiary Claim',
};

// plan assignment codes
const PLAN_ASSIGNMENT_CODES = {
	A: 'A',
	B: 'B',
	C: 'C',
};

// claim related causes codes
const CLAIM_RELATED_CAUSES_CODES = {
	EM: 'EM',
	AA: 'AA',
	OA: 'OA',
};

// yes no codes
const YES_NO_CODES = {
	Y: 'Y',
	N: 'N',
};

// ssn and ein codes
const SSN_AND_EIN_CODES = {
	ssn: 'ssn',
	ein: 'ein',
};

// insurer identification
const insurerIdentification = {
	payorIdentification: [
		{
			code: 'PI',
			codeSystem: systemMapping['NM1-08'],
			valueSystem: systemMapping['NM1-09'],
		},
		{
			code: '2U',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	medicareMedicaidPlanId: [
		{
			code: 'XV',
			codeSystem: systemMapping['NM1-08'],
			valueSystem: systemMapping['NM1-09'],
		},
	],
	employerIdentificationNumber: [
		{
			code: 'EI',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	claimOfficeNumber: [
		{
			code: 'FY',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	NAICCode: [
		{
			code: 'NF',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	providerCommercialNumber: [
		{
			code: 'G2',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	locationNumber: [
		{
			code: 'LU',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
};

// institutional identifier key and code mapping
const institutionalIdentifierKeyAndCodeMapping: InstitutionalClaimParser.IdentifierKeyCodeMappings = {
	insurer: insurerIdentification,
	otherInsurer: {
		...insurerIdentification,
		priorAuthorizationNumber: [
			{
				code: 'G1',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		referralNumber: [
			{
				code: '9F',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		claimAdjudicationIndicator: [
			{
				code: 'T4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		originalReferenceNumber: [
			{
				code: 'F8',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	billingProvider: {
		npi: [
			{
				code: 'XX',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping.npiSystem,
			},
		],
		ein: [
			{
				code: 'EI',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	subscriber: {
		standardUniqueHealthIdentifier: [
			{
				code: 'II',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
		memberId: [
			{
				code: 'MI',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
		ssn: [
			{
				code: 'SY',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		agencyClaimNumber: [
			{
				code: 'Y4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		medicalRecordIdentificationNumber: [
			{
				code: 'EA',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	patient: {
		agencyClaimNumber: [
			{
				code: 'Y4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		ssn: [
			{
				code: 'SY',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		memberId: [
			{
				code: '1W',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		medicalRecordIdentificationNumber: [
			{
				code: 'EA',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	submitter: {
		etin: [
			{
				code: '46',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
	},
	receiver: {
		etin: [
			{
				code: '46',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
	},
	payToPlan: {
		payorIdentification: [
			{
				code: 'PI',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
			{
				code: '2U',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		medicareMedicaidPlanId: [
			{
				code: 'XV',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
		employerIdentificationNumber: [
			{
				code: 'EI',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		claimOfficeNumber: [
			{
				code: 'FY',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		NAICCode: [
			{
				code: 'NF',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	claim: {
		projectCode: [
			{
				code: 'P4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		proApprovalNumber: [
			{
				code: 'G4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		specialPaymentReferenceNumber: [
			{
				code: '4N',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		referralNumber: [
			{
				code: '9F',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		priorAuthorizationNumber: [
			{
				code: 'G1',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		originalReferenceNumber: [
			{
				code: 'F8',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		repricedClaimReferenceNumber: [
			{
				code: '9A',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		adjustedRepricedClaimReferenceNumber: [
			{
				code: '9C',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		qualifiedProductsList: [
			{
				code: 'LX',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		claimNumber: [
			{
				code: 'D9',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		locationNumber: [
			{
				code: 'LU',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		medicalRecordIdentificationNumber: [
			{
				code: 'EA',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	careTeam: {
		npi: [
			{
				code: 'XX',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping.npiSystem,
			},
		],
		stateLicenseNumber: [
			{
				code: '0B',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		providerUPINNumber: [
			{
				code: '1G',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		providerCommercialNumber: [
			{
				code: 'G2',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		locationNumber: [
			{
				code: 'LU',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	serviceFacility: {
		npi: [
			{
				code: 'XX',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping.npiSystem,
			},
		],
		providerCommercialNumber: [
			{
				code: 'G2',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		locationNumber: [
			{
				code: 'LU',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		stateLicenseNumber: [
			{
				code: '0B',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
};

// professional identifier key and code mapping
const professionalIdentifierKeyAndCodeMapping: ProfessionalClaimParser.IdentifierKeyCodeMappings = {
	insurer: insurerIdentification,
	otherInsurer: {
		...insurerIdentification,
		priorAuthorizationNumber: [
			{
				code: 'G1',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		referralNumber: [
			{
				code: '9F',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		claimAdjudicationIndicator: [
			{
				code: 'T4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		originalReferenceNumber: [
			{
				code: 'F8',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	billingProvider: {
		npi: [
			{
				code: 'XX',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping.npiSystem,
			},
		],
		ein: [
			{
				code: 'EI',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		ssn: [
			{
				code: 'SY',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		stateLicenseNumber: [
			{
				code: '0B',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		providerUPINNumber: [
			{
				code: '1G',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	subscriber: {
		standardUniqueHealthIdentifier: [
			{
				code: 'II',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
		memberId: [
			{
				code: 'MI',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
		ssn: [
			{
				code: 'SY',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		agencyClaimNumber: [
			{
				code: 'Y4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		medicalRecordIdentificationNumber: [
			{
				code: 'EA',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	submitter: {
		etin: [
			{
				code: '46',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
	},
	receiver: {
		etin: [
			{
				code: '46',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
	},
	payToPlan: {
		payorIdentification: [
			{
				code: 'PI',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
			{
				code: '2U',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		medicareMedicaidPlanId: [
			{
				code: 'XV',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping['NM1-09'],
			},
		],
		employerIdentificationNumber: [
			{
				code: 'EI',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		claimOfficeNumber: [
			{
				code: 'FY',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		NAICCode: [
			{
				code: 'NF',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	claim: {
		cliaNumber: [
			{
				code: 'X4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		adjustedRepricedClaimReferenceNumber: [
			{
				code: '9C',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		facilityIdNumber: [
			{
				code: '1J',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		projectCode: [
			{
				code: 'P4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		qualifiedProductsList: [
			{
				code: 'LX',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		claimNumber: [
			{
				code: 'D9',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		mammographyCertificationNumber: [
			{
				code: 'EW',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		medicareVersionCode: [
			{
				code: 'F5',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		priorAuthorizationNumber: [
			{
				code: 'G1',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		originalReferenceNumber: [
			{
				code: 'F8',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		referralNumber: [
			{
				code: '9F',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		repricedClaimReferenceNumber: [
			{
				code: '9A',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		medicalRecordIdentificationNumber: [
			{
				code: 'EA',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		specialPaymentReferenceNumber: [
			{
				code: '4N',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	careTeam: {
		npi: [
			{
				code: 'XX',
				codeSystem: systemMapping['NM1-08'],
				valueSystem: systemMapping.npiSystem,
			},
		],
		stateLicenseNumber: [
			{
				code: '0B',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		providerUPINNumber: [
			{
				code: '1G',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		providerCommercialNumber: [
			{
				code: 'G2',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		locationNumber: [
			{
				code: 'LU',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
	lineItem: {
		cliaNumber: [
			{
				code: 'X4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		batchNumber: [
			{
				code: 'BT',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		providerControlNumber: [
			{
				code: '6R',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		mammographyCertificationNumber: [
			{
				code: 'EW',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		referralNumber: [
			{
				code: '9F',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		facilityCertificationNumber: [
			{
				code: 'F4',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		repricedLineItemReferenceNumber: [
			{
				code: '9A',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		adjustedRepricedLineItemReferenceNumber: [
			{
				code: '9D',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
		priorAuthorizationNumber: [
			{
				code: 'G1',
				codeSystem: systemMapping['REF-01'],
				valueSystem: systemMapping['REF-02'],
			},
		],
	},
};

// payer responsibility sequence mapping
const payerResponsibilitySequenceMapping = {
	1: {
		code: 'P',
		order: 1,
		display: 'Primary',
		tagColor: ANT_TAG_COLORS.purple,
	},
	2: {
		code: 'S',
		order: 2,
		display: 'Secondary',
		tagColor: ANT_TAG_COLORS.magenta,
	},
	3: {
		code: 'T',
		order: 3,
		display: 'Tertiary',
		tagColor: ANT_TAG_COLORS.blue,
	},
	4: {
		code: 'A',
		order: 4,
		display: 'Payer Responsibility Four',
		tagColor: ANT_TAG_COLORS.orange,
	},
	5: {
		code: 'B',
		order: 5,
		display: 'Payer Responsibility Five',
		tagColor: ANT_TAG_COLORS.orange,
	},
	6: {
		code: 'C',
		order: 6,
		display: 'Payer Responsibility Six',
		tagColor: ANT_TAG_COLORS.orange,
	},
	7: {
		code: 'D',
		order: 7,
		display: 'Payer Responsibility Seven',
		tagColor: ANT_TAG_COLORS.orange,
	},
	8: {
		code: 'E',
		order: 8,
		display: 'Payer Responsibility Eight',
		tagColor: ANT_TAG_COLORS.orange,
	},
	9: {
		code: 'F',
		order: 9,
		display: 'Payer Responsibility Nine',
		tagColor: ANT_TAG_COLORS.orange,
	},
	10: {
		code: 'G',
		order: 10,
		display: 'Payer Responsibility Ten',
		tagColor: ANT_TAG_COLORS.orange,
	},
	11: {
		code: 'H',
		order: 11,
		display: 'Payer Responsibility Eleven',
		tagColor: ANT_TAG_COLORS.orange,
	},
	unknown: {
		code: 'U',
		order: 12,
		display: 'Unknown',
		tagColor: ANT_TAG_COLORS.default,
	},
};

const MODIFIER_CODES_KEYS = ['A', 'B', 'C', 'D'];

// institutional care team code mapping
const institutionalCareTeamCodeMapping: Record<string, { key: keyof InstitutionalClaimParser.ICareTeamMeta; display: string }> = {
	71: { key: 'attendingPhysician', display: 'Attending Physician' },
	72: { key: 'operatingPhysician', display: 'Operating Physician' },
	ZZ: { key: 'otherOperatingPhysician', display: 'Other Operating Physician' },
	82: { key: 'renderingProvider', display: 'Rendering Provider' },
	DN: { key: 'referringProvider', display: 'Referring Provider' },
};

// institutional care team code options
const institutionalCareTeamCodeOptions = _.map(institutionalCareTeamCodeMapping, (value, key) => ({
	key,
	label: value.display,
	value: key,
}));

// poa indicator options
const poaCodeOptions = [
	{ key: 'Y', label: 'Y', value: 'Y' },
	{ key: 'N', label: 'N', value: 'N' },
	{ key: 'U', label: 'U', value: 'U' },
	{ key: 'W', label: 'W', value: 'W' },
];

// professional care team code mapping
const professionalCareTeamCodeMapping: Record<string, { key: keyof ProfessionalClaimParser.ICareTeamMeta; display: string }> = {
	DN: { key: 'referringProvider', display: 'Referring Provider' },
	DK: { key: 'orderingProvider', display: 'Ordering Provider' },
	P3: { key: 'primaryCareProvider', display: 'Primary Care Provider' },
	82: { key: 'renderingProvider', display: 'Rendering Provider' },
	DQ: { key: 'supervisingPhysician', display: 'Supervising Physician' },
};

// professional event date code mapping
const professionalEventDateCodeMapping: Record<string, keyof ProfessionalClaimParser.IEventsMeta> = {
	439: 'accidentDateMeta',
	431: 'onsetOfInjuryMeta',
	454: 'initialTreatmentDateMeta',
	314: 'disabilityDatePeriodMeta',
	435: 'admissionDateMeta',
	'096': 'dischargeDateMeta',
	360: 'initialDisabilityDateMeta',
	361: 'initialDisabilityEndDateMeta',
	304: 'lastVisitDateMeta',
};

// claim frequency mapping
const CLAIM_FREQUENCY_MAP = {
	0: {
		original: 'Non-payment/zero claims',
		short: 'Zero-Payment',
	},
	1: {
		original: 'Admit thru discharge claim',
		short: 'Original',
	},
	2: {
		original: 'Interim — first claim',
		short: 'Interim Claim – First',
	},
	3: {
		original: 'Interim — continuing claim',
		short: 'Interim Claim – Continuing',
	},
	4: {
		original: 'Interim — last claim',
		short: 'Interim Claim – Final',
	},
	5: {
		original: 'Late charge(s) only claim',
		short: 'Late Charges Only',
	},
	7: {
		original: 'Replacement of prior claim',
		short: 'Replacement',
	},
	8: {
		frequencyCode: '8',
		original: 'Void/cancel prior claim',
		short: 'Void/Cancel',
	},
	9: {
		original: 'Final claim (for HH PPS = process as a debit/credit to RAP claim)',
		short: 'Final Claim (HH PPS)',
	},
	G: {
		original: 'Common Working File (NCH) generated adjustment claim',
		short: 'CWF Adjustment',
	},
	H: {
		original: 'CMS generated adjustment claim',
		short: 'CMS Adjustment',
	},
	I: {
		original: 'Misc. adjustment claim (e.g., initiated by intermediary or QIO)',
		short: 'Miscellaneous Adjustment',
	},
	J: {
		original: 'Other adjustment request',
		short: 'Other Adjustment',
	},
	K: {
		original: 'OIG Initiated Adjustment Claim',
		short: 'OIG Adjustment',
	},
	M: {
		original: 'Medicare secondary payer (MSP) adjustment',
		short: 'MSP Adjustment',
	},
	P: {
		original: 'Adjustment required by QIO',
		short: 'QIO-Required Adjustment',
	},
	Q: {
		original: 'Claim Submitted for Reconsideration Outside of Timely Limits',
		short: 'Reconsideration (Late Submission)',
	},
	Y: {
		original: 'Replacement of prior abbreviated encounter submission',
		short: 'Replaced Encounter Submission',
	},
};

const ACTIVITY_NAMES = {
	PaymentPosted: 'Payment Posted',
};

const HCPCS_SYSTEMS = [systemMapping.hcpcsCode, systemMapping.wk, systemMapping.hp];

const PAYER_RESPONSIBILITY_OPTIONS = _.map(
	_.filter(payerResponsibilitySequenceMapping, (sequence) => sequence?.code !== payerResponsibilitySequenceMapping.unknown.code),
	(value) => ({
		label: value.display,
		value: value.code,
		order: value.order,
		tagColor: value.tagColor,
	}),
);

const CLAIM_FREQUENCY_OPTIONS = _.map(CLAIM_FREQUENCY_MAP, (value, index) => ({
	label: value.short,
	value: index,
}));

const PAYER_SEQUENCE_OPTIONS = [
	{
		key: 'primary',
		label: 'Primary',
		value: payerResponsibilitySequenceMapping[1]?.order,
	},
	{
		key: 'secondary',
		label: 'Secondary',
		value: payerResponsibilitySequenceMapping[2]?.order,
	},
];

const VOID_CLAIM_FREQUENCY_KEY = _.findKey(CLAIM_FREQUENCY_MAP, { short: 'Void/Cancel' });

export {
	CLAIM_TYPES,
	CLAIM_RELATED_CAUSES_CODES,
	YES_NO_CODES,
	SSN_AND_EIN_CODES,
	PLAN_ASSIGNMENT_CODES,
	institutionalIdentifierKeyAndCodeMapping,
	professionalIdentifierKeyAndCodeMapping,
	payerResponsibilitySequenceMapping,
	institutionalCareTeamCodeMapping,
	professionalCareTeamCodeMapping,
	professionalEventDateCodeMapping,
	CLAIM_FREQUENCY_MAP,
	RELATED_CLAIM_CODE_DISPLAY_MAPPING,
	CLAIM_WORK_STATES,
	ACTIVITY_NAMES,
	HCPCS_SYSTEMS,
	PAYER_RESPONSIBILITY_OPTIONS,
	institutionalCareTeamCodeOptions,
	poaCodeOptions,
	CLAIM_FREQUENCY_OPTIONS,
	GROUPED_CLAIM_WORK_STATES,
	ACTIVE_AND_INACTIVE_CLAIM_WORK_STATES,
	PAYER_SEQUENCE_OPTIONS,
	MODIFIER_CODES_KEYS,
	VOID_CLAIM_FREQUENCY_KEY,
};
